import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, Plus, Loader2, Pencil, Trash2, CheckCircle, Clock, AlertCircle, MessageSquare, Info } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = ['Cash', 'Check', 'Zelle', 'Credit Card', 'Debit Card', 'Wire Transfer', 'Other'];

const statusBadge = {
  completed: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-amber-100 text-amber-800',
  overdue: 'bg-rose-100 text-rose-800'
};

const emptyForm = {
  client_id: '',
  amount: '',
  payment_date: format(new Date(), 'yyyy-MM-dd'),
  payment_method: '',
  status: 'completed',
  notes: '',
  reference_id: '',
  installment_number: '',
  total_installments: ''
};

export default function FinancePage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmSending, setConfirmSending] = useState(null);
  const [confirmSent, setConfirmSent] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients`),
        axios.get(`${API_URL}/api/payments`)
      ]);
      setClients(clientsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Totals
  const totalReceived = payments.filter(p => p.status === 'completed').reduce((s, p) => s + (p.amount || 0), 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((s, p) => s + (p.amount || 0), 0);
  const totalOverdue = payments.filter(p => p.status === 'overdue').reduce((s, p) => s + (p.amount || 0), 0);

  const openAddDialog = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEditDialog = (payment) => {
    setEditingId(payment.id);
    setForm({
      client_id: payment.client_id || '',
      amount: payment.amount?.toString() || '',
      payment_date: payment.payment_date || '',
      payment_method: payment.payment_method || '',
      status: payment.status || 'completed',
      notes: payment.notes || '',
      reference_id: payment.reference_id || '',
      installment_number: payment.installment_number?.toString() || '',
      total_installments: payment.total_installments?.toString() || ''
    });
    setShowDialog(true);
  };

  // Compute installment progress for the selected client
  const selectedClient = clients.find(c => c.id === form.client_id);
  const clientTotalInstallments = selectedClient?.number_of_installments || selectedClient?.installments || 0;
  const clientMonthlyAmount = selectedClient?.monthly_payment_amount || 0;
  const clientPaidInstallments = form.client_id
    ? payments.filter(p => p.client_id === form.client_id && p.status === 'completed').length
    : 0;
  const paidInstallmentNumbers = form.client_id
    ? payments
        .filter(p => p.client_id === form.client_id && p.status === 'completed' && p.installment_number)
        .map(p => p.installment_number)
    : [];
  const suggestedNextInstallment = clientTotalInstallments > 0 ? clientPaidInstallments + 1 : 0;
  const isDuplicate = form.installment_number && paidInstallmentNumbers.includes(parseInt(form.installment_number));

  // Auto-fill installment info when client changes
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    const total = client?.number_of_installments || client?.installments || 0;
    const monthlyAmt = client?.monthly_payment_amount || 0;
    const paidCount = payments.filter(p => p.client_id === clientId && p.status === 'completed').length;
    const nextInstallment = total > 0 ? paidCount + 1 : '';

    setForm(f => ({
      ...f,
      client_id: clientId,
      amount: monthlyAmt > 0 ? monthlyAmt.toString() : f.amount,
      installment_number: nextInstallment > total ? '' : nextInstallment.toString(),
      total_installments: total > 0 ? total.toString() : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.amount) return;
    setFormLoading(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        installment_number: form.installment_number ? parseInt(form.installment_number) : null,
        total_installments: form.total_installments ? parseInt(form.total_installments) : null
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/payments/${editingId}`, payload);
      } else {
        await axios.post(`${API_URL}/api/payments`, payload);
      }
      setShowDialog(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API_URL}/api/payments/${paymentId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const handleSendConfirmation = async (payment) => {
    setConfirmSending(payment.id);
    try {
      await axios.post(`${API_URL}/api/payment-reminders/mark-sent`, {
        client_id: payment.client_id,
        due_date: payment.payment_date,
        amount: payment.amount,
        sms_type: 'confirmation'
      });
      setConfirmSent(prev => ({ ...prev, [payment.id]: true }));
    } catch (error) {
      console.error('Error sending confirmation:', error);
    } finally {
      setConfirmSending(null);
    }
  };

  const getClientName = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    return c ? `${c.first_name} ${c.last_name}` : 'Unknown';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="finance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Payments</h1>
            <p className="text-slate-500 mt-1">Manual payment tracking</p>
          </div>
          <Button
            className="bg-[#1a2e5a] hover:bg-[#15254a] text-white"
            onClick={openAddDialog}
            data-testid="add-payment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Payment
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm" data-testid="total-received">
            <CardContent className="p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Received</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">${totalReceived.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-l-4 border-l-amber-500 shadow-sm" data-testid="total-pending">
            <CardContent className="p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">${totalPending.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-l-4 border-l-rose-500 shadow-sm" data-testid="total-overdue">
            <CardContent className="p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Overdue</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">${totalOverdue.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Payments Table */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="payments-table">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">All Payments ({payments.length})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {payments.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No payments recorded yet</p>
                <p className="text-slate-400 text-sm mt-1">Click "Add Payment" to create the first entry</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Client</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Amount</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Installment</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Method</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Status</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Reference</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">Notes</TableHead>
                    <TableHead className="text-xs font-medium text-slate-500 uppercase w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} className="hover:bg-slate-50" data-testid={`payment-row-${payment.id}`}>
                      <TableCell className="font-medium text-slate-900">{getClientName(payment.client_id)}</TableCell>
                      <TableCell className="text-slate-700">{payment.payment_date || '-'}</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">${payment.amount?.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {payment.installment_number && payment.total_installments
                          ? <div>
                              <span className="font-medium">{payment.installment_number} of {payment.total_installments}</span>
                              <span className="block text-xs text-slate-400">Remaining: {Math.max(payment.total_installments - payment.installment_number, 0)}</span>
                            </div>
                          : '-'
                        }
                      </TableCell>
                      <TableCell className="text-slate-600">{payment.payment_method || '-'}</TableCell>
                      <TableCell>
                        <Badge className={statusBadge[payment.status] || statusBadge.pending}>
                          {payment.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{payment.reference_id || '-'}</TableCell>
                      <TableCell className="text-slate-500 text-sm max-w-[150px] truncate">{payment.notes || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {payment.status === 'completed' && (
                            <button
                              onClick={() => handleSendConfirmation(payment)}
                              disabled={confirmSent[payment.id] || confirmSending === payment.id}
                              className={`p-1.5 rounded-md transition-colors ${
                                confirmSent[payment.id]
                                  ? 'bg-emerald-50 text-emerald-500 cursor-default'
                                  : 'hover:bg-emerald-50 text-slate-400 hover:text-emerald-600'
                              }`}
                              title={confirmSent[payment.id] ? 'Confirmation sent' : 'Send Payment Confirmation SMS'}
                              data-testid={`confirm-sms-${payment.id}`}
                            >
                              {confirmSending === payment.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : confirmSent[payment.id] ? (
                                <CheckCircle className="w-3.5 h-3.5" />
                              ) : (
                                <MessageSquare className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => openEditDialog(payment)}
                            className="p-1.5 rounded-md hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                            title="Edit"
                            data-testid={`edit-payment-${payment.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(payment.id)}
                            className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                            title="Delete"
                            data-testid={`delete-payment-${payment.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Payment Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Payment' : 'Add Payment'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={handleClientChange}>
                <SelectTrigger data-testid="payment-client-select">
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Installment Progress */}
            {form.client_id && clientTotalInstallments > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5" data-testid="installment-progress">
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Info className="w-4 h-4 text-blue-500" />
                  Installment Progress
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-slate-500">Total Plan:</span>
                  <span className="font-medium text-slate-900">{clientTotalInstallments} Installments</span>
                  <span className="text-slate-500">Already Paid:</span>
                  <span className="font-medium text-slate-900">{clientPaidInstallments}</span>
                  <span className="text-slate-500">Now Recording:</span>
                  <span className="font-semibold text-[#1a2e5a]">
                    {form.installment_number
                      ? `Installment ${form.installment_number} of ${form.total_installments || clientTotalInstallments}`
                      : 'Select below'
                    }
                  </span>
                  <span className="text-slate-500">Remaining After:</span>
                  <span className="font-medium text-slate-900">
                    {form.installment_number
                      ? Math.max(clientTotalInstallments - parseInt(form.installment_number), 0)
                      : clientTotalInstallments - clientPaidInstallments
                    }
                  </span>
                </div>
                {/* Progress bar */}
                <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
                  <div
                    className="bg-[#1a2e5a] h-2 rounded-full transition-all"
                    style={{ width: `${(clientPaidInstallments / clientTotalInstallments) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Installment Selector */}
            {form.client_id && clientTotalInstallments > 0 && (
              <div>
                <Label>Installment *</Label>
                <Select
                  value={form.installment_number}
                  onValueChange={(v) => setForm(f => ({ ...f, installment_number: v, total_installments: clientTotalInstallments.toString() }))}
                >
                  <SelectTrigger data-testid="payment-installment-select">
                    <SelectValue placeholder="Select installment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: clientTotalInstallments }, (_, i) => i + 1).map(num => {
                      const isPaid = paidInstallmentNumbers.includes(num);
                      return (
                        <SelectItem key={num} value={num.toString()}>
                          Installment {num} of {clientTotalInstallments}
                          {isPaid ? ' (Paid)' : num === suggestedNextInstallment ? ' (Next)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {isDuplicate && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-amber-600" data-testid="duplicate-warning">
                    <AlertCircle className="w-3.5 h-3.5" />
                    This installment was already paid. Recording again will create a duplicate entry.
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm(f => ({...f, amount: e.target.value}))}
                  placeholder="0.00"
                  required
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setForm(f => ({...f, payment_date: e.target.value}))}
                  required
                  data-testid="payment-date-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm(f => ({...f, payment_method: v}))}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue placeholder="Select method..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reference / Transaction ID</Label>
              <Input
                value={form.reference_id}
                onChange={(e) => setForm(f => ({...f, reference_id: e.target.value}))}
                placeholder="e.g., TXN-123456"
                data-testid="payment-reference-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(f => ({...f, notes: e.target.value}))}
                placeholder="Optional notes..."
                rows={2}
                data-testid="payment-notes-input"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                className="bg-[#1a2e5a] hover:bg-[#15254a]"
                disabled={formLoading || !form.client_id || !form.amount}
                data-testid="submit-payment-btn"
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingId ? 'Save Changes' : 'Add Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
